import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, X, Send, LifeBuoy, Bug } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

export default function SupportWidget({ variant = "mobile" }) {
  const [open, setOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [bugMsg, setBugMsg] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  const loadThread = async () => {
    try {
      const { data } = await api.get("/support/thread");
      setMessages(data.messages || []);
    } catch (e) { /* noop */ }
    setLoaded(true);
  };

  useEffect(() => { if (open && !loaded) loadThread(); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const optimistic = [...messages, { from: "user", text: text.trim(), at: new Date().toISOString() }];
    setMessages(optimistic);
    setText("");
    try {
      const { data } = await api.post("/support/message", { text: optimistic[optimistic.length - 1].text, channel: "chat" });
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Invio non riuscito");
    }
    setSending(false);
  };

  const sendBug = async () => {
    if (!bugMsg.trim()) return toast.error("Scrivi un messaggio");
    try { await api.post("/beta/report-bug", { message: bugMsg.trim() }); toast.success("Grazie! Segnalazione inviata."); setBugOpen(false); setBugMsg(""); }
    catch (e) { toast.error("Errore nell'invio"); }
  };

  const btn = "w-8 h-8 rounded-lg border border-slate-200 bg-card text-slate-500 hover:text-primary hover:border-primary/40 flex items-center justify-center transition-colors";
  const pos = variant === "desktop" ? "fixed top-[4.25rem] left-4" : "fixed top-16 left-3";

  return (
    <>
      <div className="flex items-center gap-1.5" data-testid={`header-tools-${variant}`}>
        <button data-testid={`header-bug-btn-${variant}`} title="Segnala un bug / idea" onClick={() => setBugOpen(true)} className={btn}>
          <Bug style={{ width: 15, height: 15 }} />
        </button>
        <button data-testid={`support-fab-${variant}`} title="Chat di supporto" onClick={() => setOpen((v) => !v)} className={`${btn} ${open ? "text-primary border-primary/40" : ""}`}>
          {open ? <X style={{ width: 15, height: 15 }} /> : <LifeBuoy style={{ width: 15, height: 15 }} />}
        </button>
      </div>

      <Dialog open={bugOpen} onOpenChange={setBugOpen}>
        <DialogContent className="max-w-md" data-testid="beta-bug-dialog">
          <DialogHeader>
            <DialogTitle>Segnala un Bug o Suggerisci un'idea</DialogTitle>
            <DialogDescription>Il tuo contributo aiuta a migliorare MyQuoteFlow.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="beta-bug-message" rows={4} value={bugMsg} onChange={(e) => setBugMsg(e.target.value)} placeholder="Descrivi il problema o la tua idea…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBugOpen(false)}>Annulla</Button>
            <Button data-testid="beta-bug-send" onClick={sendBug}>Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {open && (
        <div data-testid="support-panel" className={`${pos} z-50 w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-2xl bg-card border border-slate-200 shadow-2xl overflow-hidden flex flex-col`} style={{ height: "min(460px, calc(100vh - 9rem))" }}>
          <div className="bg-primary text-white px-4 py-3 flex items-center gap-2">
            <MessageCircle style={{ width: 18, height: 18 }} />
            <div>
              <p className="text-sm font-bold leading-tight">Assistenza MyQuoteFlow</p>
              <p className="text-[11px] text-indigo-100">Rispondiamo in pochi minuti</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50">
            {messages.length === 0 && (
              <p className="text-center text-sm text-slate-400 mt-6">Ciao! 👋 Come possiamo aiutarti?<br />Scrivici un messaggio qui sotto.</p>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`support-msg-${m.from}`} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.from === "user" ? "bg-primary text-white" : m.from === "admin" ? "bg-emerald-100 text-emerald-900" : "bg-card border border-slate-200 text-slate-700"}`}>
                  {m.from === "admin" && <span className="block text-[10px] font-semibold text-emerald-600 mb-0.5">Team MyQuoteFlow</span>}
                  {m.text}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-2.5 border-t border-slate-200 flex gap-2">
            <Input data-testid="support-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Scrivi un messaggio…" />
            <Button data-testid="support-send" onClick={send} disabled={sending || !text.trim()} className="shrink-0 px-3"><Send style={{ width: 16, height: 16 }} /></Button>
          </div>
        </div>
      )}
    </>
  );
}
